import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { FinanceStatisticsEntity } from "@modules/finances/entities/financeStatisticsEntity"
import { getFinanceStatisticsStatusIcon, getFinanceStatisticsStatusText } from "@modules/finances/utils/financeUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUText } from "@modules/ui/components/huText"
import { HURow, HUStack } from "@modules/ui/components/layout"
import TooltipIndicator from "@modules/ui/components/tooltipIndicator"
import { animatedSkeletonStyle } from "@modules/ui/styles/animation"
import dayjs from "dayjs"
import { Tooltip } from "primereact/tooltip"
import styled, { DefaultTheme, useTheme } from "styled-components"

type CardType = "Primary" | "Secondary"
type Props = {
  cardType: CardType
  cardTitle: string
  loading: boolean
  stats?: FinanceStatisticsEntity | null
} & React.HTMLAttributes<HTMLDivElement>

export const FinanceMonthlyTransferCard: React.FC<Props> = ({ cardType, cardTitle, loading, stats, ...props }) => {
  const language = useLanguage()
  const theme = useTheme()
  const color = cardType === "Primary" ? theme.buttonPrimaryTextColor : theme.textPrimary
  const remainingBalance = (stats?.pendingBalanceForTransfer ?? 0) + (stats?.availableBalanceForTransfer ?? 0)
  return (
    <Container $cardType={cardType} $loading={loading} {...props}>
      <HURow className="pl-2 pr-0">
        <HUStack className="w-6 gap-0" style={{ wordWrap: "break-word" }}>
          <HURow align gap={10}>
            <Dot />
            <HUText fontStyle="BL" color={color}>
              {cardTitle}
            </HUText>
          </HURow>
          <HUText fontStyle="DS" color={color}>
            {stats?.transferAmount
              ? formatCurrency(stats.transferAmount, stats.totalTransactionsCurrency, language)
              : "--"}
          </HUText>
          {/* <HUText fontStyle="BM" color={theme.neutralColor600}>
            {`${firstDate} ${word("global.periodInterval.to")} ${lastDate}`}
          </HUText> */}
        </HUStack>
        <HUStack className="w-6 align-items-end align-self-stretch z-1">
          <HURow gap={10} className="align-items-center w-full">
            <HUText fontStyle="BS" color={theme.financeCardDataLabelColor} className="text-right w-6">
              {word("finances.page.table.header.status")}
            </HUText>
            <HUText fontStyle="LL" color={color} className="w-6 flex flex-row gap-2 align-items-center">
              {getFinanceStatisticsStatusText(stats?.transferStatus)}
              <div>{getFinanceStatisticsStatusIcon(theme.textPrimary, stats?.transferStatus)}</div>
            </HUText>
          </HURow>
          <HURow gap={10} className="align-items-center w-full">
            <HUText fontStyle="BS" color={theme.financeCardDataLabelColor} className="text-right w-6">
              {word("finances.page.paymentSentDate")}
            </HUText>
            <HUText fontStyle="LL" color={color} className="w-6">
              {stats?.transferArrivalDate ? dayjs(stats.transferArrivalDate).format("L") : "--"}
            </HUText>
          </HURow>
          <HURow gap={10} className="align-items-center w-full">
            <HUText
              className="tooltip-remaining-balance flex flex-row align-items-center cursor-pointer w-6 justify-content-end"
              data-pr-showdelay={200}
              data-pr-position="bottom"
              color={theme.financeCardDataLabelColor}
              fontStyle="BS"
            >
              <TooltipContainer className="tooltip-container" target=".tooltip-remaining-balance">
                <HUText fontStyle="BL" className="tooltip-title flex flex-row align-items-center">
                  <TooltipIndicator className="mr-2 black" />
                  {word("finances.page.remainingBalance")}
                </HUText>
                <div className="tooltip-content text-justify">
                  <HUText type="label" fontStyle="BM">
                    {word("finance.tooltip.remainingBalance.description")}
                  </HUText>
                </div>
              </TooltipContainer>
              {word("finances.page.remainingBalance")}
              <TooltipIndicator className="ml-2" color={theme.blackAtheleWhiteOwner} />
            </HUText>
            <HUText fontStyle="LL" color={color} className="w-6">
              {remainingBalance > 0
                ? formatCurrency(remainingBalance, stats?.totalTransactionsCurrency, language)
                : "--"}
            </HUText>
          </HURow>
        </HUStack>
      </HURow>
      <HustleupSmallIconStyled />
    </Container>
  )
}

const Container = styled.div<{ $cardType: CardType; $loading: boolean }>`
  height: 172px;
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  background: ${({ $cardType, theme }) =>
    $cardType === "Primary"
      ? `linear-gradient(90deg, ${theme.primaryColor} 0%, ${theme.primaryColor600} 100%)`
      : theme.surfaceCard};
  padding: 25px 0px 20px 28px;
  border-radius: 33px;
  gap: 10px;

  ${({ $cardType, $loading, theme }) =>
    $loading &&
    ($cardType === "Primary"
      ? animatedSkeletonStyle(theme as DefaultTheme, theme.primaryColor, theme.primaryColor600)
      : animatedSkeletonStyle(theme as DefaultTheme, theme.surfaceCard))}
`

const HustleupSmallIconStyled = styled(HustleupSmallIcon)`
  position: absolute;
  top: 0;
  right: 0;
  opacity: ${({ theme }) => theme.financeCardBackgroundIconOpacity};
`

const Dot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 5.6px;
  background: ${({ theme }) => theme.textPrimary};
`

const TooltipContainer = styled(Tooltip)`
  .p-tooltip-text {
    max-width: 400px;
  }
`
